import {
  propertyMapFromList,
  rowMatchesSearch,
  type RowFilterContext,
} from '@couplespace/core/databases';

import type { BlockRecord } from '../blocks/types';
import type { BlockPropertyRecord, DatabaseQueryResult } from './types';

function rowContext(
  row: BlockRecord,
  propertiesByRowId: Record<string, BlockPropertyRecord[]>,
): RowFilterContext {
  const props = propertiesByRowId[row.id] ?? [];
  return {
    title: row.title,
    properties: propertyMapFromList(
      props.map((p) => ({ propertyId: p.propertyId, value: p.value })),
    ),
  };
}

/** Apply toolbar search when the API did not (or returned extra rows). */
export function applyDatabaseSearch(
  result: DatabaseQueryResult,
  search: string,
): DatabaseQueryResult {
  const needle = search.trim();
  if (!needle) {
    return result;
  }

  if (result.groups?.length) {
    const groups = result.groups
      .map((group) => {
        const propertiesByRowId = group.propertiesByRowId ?? {};
        const rows = group.rows.filter((row) =>
          rowMatchesSearch(rowContext(row, propertiesByRowId), needle),
        );
        if (rows.length === 0) {
          return null;
        }
        const nextProps: Record<string, BlockPropertyRecord[]> = {};
        for (const row of rows) {
          const list = propertiesByRowId[row.id];
          if (list) {
            nextProps[row.id] = list;
          }
        }
        return { ...group, rows, propertiesByRowId: nextProps };
      })
      .filter((g): g is NonNullable<typeof g> => g !== null);

    return { ...result, groups, rows: [] };
  }

  const propertiesByRowId = result.propertiesByRowId ?? {};
  const rows = result.rows.filter((row) =>
    rowMatchesSearch(rowContext(row, propertiesByRowId), needle),
  );
  const nextProps: Record<string, BlockPropertyRecord[]> = {};
  for (const row of rows) {
    const list = propertiesByRowId[row.id];
    if (list) {
      nextProps[row.id] = list;
    }
  }
  return { ...result, rows, propertiesByRowId: nextProps };
}
